import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useBrand } from "@/hooks/use-brand"
import { LOCALE_CONFIG } from "@/config/locale-domains"

export function useOrganizationSchema() {
  const { t } = useTranslation()
  const brand = useBrand()
  const locale = brand.locale
  const domain = LOCALE_CONFIG[locale]?.domain || LOCALE_CONFIG.ro.domain

  return useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand.brand,
      description: t("meta.description"),
      url: `https://${domain}`,
      logo: `https://${domain}/og-default.svg`,
    }),
    [brand.brand, domain, t]
  )
}

export function useSoftwareApplicationSchema() {
  const { t } = useTranslation()
  const brand = useBrand()
  const domain = LOCALE_CONFIG[brand.locale]?.domain || LOCALE_CONFIG.ro.domain

  return useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: brand.brand,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: t("meta.description"),
      url: `https://${domain}`,
    }),
    [brand.brand, domain, t]
  )
}

export function buildFaqSchema(faqs) {
  if (!faqs?.length) return null
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  }
}

/** FAQ + OfferCatalog with RON prices aligned to the pricing page (no misleading zero-price Offer). */
export function buildPricingPageJsonLd({ domain, faqs, plans, billing, priceTable }) {
  const faqBlock = buildFaqSchema(faqs)
  const isYearly = billing === "yearly"
  const planArray = Array.isArray(plans) ? plans : []
  const itemListElement = planArray
    .map((plan, idx) => {
      const row = priceTable?.[plan.name]
      if (!row || plan.name == null) return null
      const price = isYearly ? row.yearly : row.monthly
      return {
        "@type": "ListItem",
        position: idx + 1,
        item: {
          "@type": "Offer",
          name: `${plan.name} (${isYearly ? "annual" : "monthly"})`,
          url: `https://${domain}/pricing`,
          priceCurrency: "RON",
          price: String(price),
          itemOffered: {
            "@type": "Service",
            name: plan.name,
            description: plan.tagline || undefined,
          },
        },
      }
    })
    .filter(Boolean)

  const graph = []
  if (faqBlock) graph.push(faqBlock)
  if (itemListElement.length) {
    graph.push({
      "@type": "OfferCatalog",
      name: "Subscription plans",
      url: `https://${domain}/pricing`,
      itemListElement,
    })
  }

  if (!graph.length) return null
  return { "@context": "https://schema.org", "@graph": graph }
}

export function buildBlogPostingSchema({ title, description, date, author, url, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    datePublished: date,
    author: { "@type": "Person", name: author },
    image: image || undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  }
}

export function buildContactPageSchema({ name, url, email }) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name,
    url,
    mainEntity: {
      "@type": "Organization",
      name,
      email,
    },
  }
}
